import { Builder } from '@builder.io/react';
import cn from 'classnames';
import capitalize from 'lodash-es/capitalize';
import { FC } from 'react';

import {
  BACKGROUND_COLOR_NAMES,
  CATEGORY_COLOR_NAMES,
  COLOR_NAME_BRAND,
  COLOR_NAME_PALE_GRAY,
} from '@/shared/constants/colors';
import {
  actionButtonInput,
  backgroundColorInput,
  componentLinkIdInput,
  contentMobileBehaviorInput,
} from '@/shared/inputs';
import { getBuilderSpacingClass } from '@/shared/spacing';
import {
  ContentMobileBehavior,
  CtaVariant,
  LinkTarget,
  SharedBuilderComponent,
} from '@/shared/types';

import { ActionVariant, BaseAction } from '../Action';
import { ComponentHeader } from '../ComponentHeader/ComponentHeader';
import { flatMediaInput } from '../Media';
import sharedStyle from '../shared.module.scss';
import styles from './ContentImage.module.scss';
import { Row, RowInput } from './Row';
import { RowTitleTypography } from './types';

interface BuilderInput extends SharedBuilderComponent {
  componentLinkId?: string;
  title?: string;
  description?: string;
  fullWidth: boolean;
  ctaVariant: CtaVariant;
  ctaColor: string;
  rowTitleTypography: RowTitleTypography;
  rows: RowInput[];
  contentMobileBehavior: ContentMobileBehavior;
  action: BaseAction;
  actionVariant: ActionVariant;
}

type Props = BuilderInput & {
  locale: string;
};

export const ContentImage: FC<Props> = ({
  componentLinkId,
  title,
  description,
  fullWidth = true,
  ctaVariant,
  ctaColor,
  rowTitleTypography = 'h3',
  rows = [],
  backgroundColorClassName = 'tw:bg-neutral-tertiary',
  contentMobileBehavior = 'scroll',
  action,
  actionVariant,
  attributes,
  builderBlock,
  builderState,
  locale,
}) => {
  const spacingStyles = getBuilderSpacingClass(builderBlock, builderState);
  const scrolling = contentMobileBehavior === 'scroll';
  const swiping = contentMobileBehavior === 'swipe';

  // When open page in editor or LivePreview mode, Builder doesn't run dataFetcher
  // causing locale value undefined. So, we use builderState locale value when
  // using component in editor and leave the locale when we generate static content
  const blockLocale = locale || builderState?.context.locale;

  return (
    <section
      {...attributes}
      id={componentLinkId}
      className={cn(
        sharedStyle.section,
        backgroundColorClassName,
        spacingStyles
      )}
    >
      <div className="u-wrapper">
        <div className={styles.container}>
          <ComponentHeader
            locale={blockLocale}
            action={{ ...action, colorName: ctaColor }}
            actionVariant={actionVariant}
            title={title}
            titleSpacingClassName={'u-m-b-xl'}
            description={description}
            fullWidth={fullWidth}
            headerClassName={cn({
              'u-m-b-xl u-m-b-lg-xxl-6': rows && rows.length > 0,
            })}
          >
            <div
              className={cn('tw:flex-row', {
                [styles.swipableContantContainer]: swiping,
              })}
            >
              {rows.map((row, index) => {
                const key = index;
                return (
                  <Row
                    key={key}
                    locale={blockLocale}
                    {...row}
                    className={cn({
                      [styles.contentSwipable]: swiping,
                      [styles.content]: scrolling,
                      'u-m-b-lg-xxl-10': scrolling,
                      'u-m-b-md-xxl-6': scrolling,
                      'u-m-b-sm-xxl-6': scrolling,
                    })}
                    ctaVariant={ctaVariant}
                    ctaColor={ctaColor}
                    rowTitleTypography={rowTitleTypography}
                  />
                );
              })}
            </div>
          </ComponentHeader>
        </div>
      </div>
    </section>
  );
};

export function dataFetcher(locale: string) {
  return async () => ({
    locale,
  });
}

export const NAME = 'ContentImage';

export function componentRegister() {
  Builder.registerComponent(ContentImage, {
    name: NAME,
    inputs: [
      componentLinkIdInput(),
      {
        name: 'title',
        friendlyName: 'Headline',
        type: 'string',
        defaultValue:
          'Section <span class="u-typography-h2-serif tw:text-brand-primary">title</span>',
        required: true,
      },
      {
        name: 'rowTitleTypography',
        friendlyName: 'Row Title Typography',
        type: 'string',
        enum: ['h2', 'h3'],
        defaultValue: 'h3',
      },
      {
        name: 'description',
        friendlyName: 'Description',
        type: 'string',
        defaultValue:
          'Section description indicating the content to be foundand presented here with a medium legth copy block.',
      },
      {
        name: 'ctaVariant',
        friendlyName: 'CTA type',
        type: 'string',
        enum: ['none', 'primary', 'secondary', 'tertiary'],
        defaultValue: 'none',
      },
      {
        name: 'rows',
        type: 'list',
        subFields: [
          { name: 'title', type: 'string', required: true },
          { name: 'description', type: 'string', required: true },
          { name: 'imageUrl', type: 'file', allowedFileTypes: ['jpeg', 'png'] },
          { name: 'ctaText', type: 'string' },
          { name: 'ctaUrl', friendlyName: 'CTA url', type: 'string' },
        ],
      },
      backgroundColorInput(COLOR_NAME_PALE_GRAY),
      contentMobileBehaviorInput(),
      ...actionButtonInput({
        allowVariants: [ActionVariant.NONE, ActionVariant.TERTIARY],
        allowColors: [COLOR_NAME_BRAND],
      }),
    ],
    noWrap: true,
  });

  const v2Name = `v2:${NAME}`;
  Builder.registerComponent(ContentImage, {
    name: v2Name,
    inputs: [
      componentLinkIdInput(),
      {
        name: 'title',
        friendlyName: 'Headline',
        type: 'CatawikiEditor',
        defaultValue:
          'Section <span class="u-typography-h2-serif tw:text-brand-primary">title</span>',
      },
      {
        name: 'rowTitleTypography',
        friendlyName: 'Row Title Typography',
        type: 'string',
        enum: ['h2', 'h3'],
        defaultValue: 'h3',
      },
      {
        name: 'description',
        friendlyName: 'Description',
        type: 'CatawikiEditor',
        defaultValue:
          'Section description indicating the content to be foundand presented here with a medium legth copy block.',
      },
      {
        name: 'fullWidth',
        friendlyName: 'Show content in full width',
        helperText: 'Show content in full width or limit to 800px',
        type: 'boolean',
        defaultValue: true,
      },
      ...actionButtonInput({
        allowVariants: [ActionVariant.NONE, ActionVariant.TERTIARY],
        allowColors: [COLOR_NAME_BRAND, ...CATEGORY_COLOR_NAMES],
      }),
      {
        name: 'ctaColor',
        friendlyName: 'Button Color',
        type: 'string',
        enum: [...BACKGROUND_COLOR_NAMES, ...CATEGORY_COLOR_NAMES],
        defaultValue: COLOR_NAME_BRAND,
      },
      {
        name: 'ctaVariant',
        friendlyName: 'Row Button Type',
        type: 'string',
        enum: ['none', 'primary', 'secondary', 'tertiary'],
        defaultValue: 'none',
      },
      {
        name: 'rows',
        type: 'list',
        subFields: [
          {
            name: 'title',
            type: 'CatawikiEditor',
            defaultValue:
              'Title of <span class="u-typography-h3-serif tw:text-brand-primary">the topic</span>',
            required: true,
          },
          {
            name: 'description',
            type: 'CatawikiEditor',
            defaultValue:
              'Section description indicating the content to be foundand presented here with a medium legth copy block. Section description indicating the content to be foundand presented here with a medium legth copy block.',
            required: true,
          },
          ...flatMediaInput({ useCatawikiPlainEditor: true }),
          {
            name: 'ctaText',
            friendlyName: 'Button Label',
            defaultValue: 'Explore more',
            type: 'CatawikiPlainEditor',
          },
          {
            name: 'ctaUrl',
            friendlyName: 'Button URL',
            type: 'CatawikiPlainEditor',
          },
          {
            name: 'ctaTarget',
            friendlyName: 'Open link target',
            type: 'string',
            enum: Object.keys(LinkTarget).map((name, index) => ({
              label: capitalize(name).split('_').join(' '),
              value: Object.values(LinkTarget)[index],
            })),
            defaultValue: '_blank',
          },
        ],
      },
      backgroundColorInput(COLOR_NAME_PALE_GRAY),
      contentMobileBehaviorInput(),
    ],
    noWrap: true,
  });

  const v3Name = `v3:${NAME}`;
  Builder.registerComponent(ContentImage, {
    name: v3Name,
    inputs: [
      componentLinkIdInput(),
      {
        name: 'title',
        friendlyName: 'Headline',
        type: 'html',
        defaultValue:
          'Section <span class="u-typography-h2-serif tw:text-brand-primary">title</span>',
      },
      {
        name: 'rowTitleTypography',
        friendlyName: 'Row Title Typography',
        type: 'string',
        enum: ['h2', 'h3'],
        defaultValue: 'h3',
      },
      {
        name: 'description',
        friendlyName: 'Description',
        type: 'html',
        defaultValue:
          'Section description indicating the content to be foundand presented here with a medium legth copy block.',
      },
      {
        name: 'fullWidth',
        friendlyName: 'Show content in full width',
        helperText: 'Show content in full width or limit to 800px',
        type: 'boolean',
        defaultValue: true,
      },
      ...actionButtonInput({
        allowVariants: [ActionVariant.NONE, ActionVariant.TERTIARY],
        allowColors: [COLOR_NAME_BRAND, ...CATEGORY_COLOR_NAMES],
        useCatawikiPlainEditor: false,
      }),
      {
        name: 'ctaColor',
        friendlyName: 'Button Color',
        type: 'string',
        enum: [...BACKGROUND_COLOR_NAMES, ...CATEGORY_COLOR_NAMES],
        defaultValue: COLOR_NAME_BRAND,
      },
      {
        name: 'ctaVariant',
        friendlyName: 'Row Button Type',
        type: 'string',
        enum: ['none', 'primary', 'secondary', 'tertiary'],
        defaultValue: 'none',
      },
      {
        name: 'rows',
        type: 'list',
        subFields: [
          {
            name: 'title',
            type: 'html',
            defaultValue:
              'Title of <span class="u-typography-h3-serif tw:text-brand-primary">the topic</span>',
            required: true,
          },
          {
            name: 'description',
            type: 'html',
            defaultValue:
              'Section description indicating the content to be foundand presented here with a medium legth copy block. Section description indicating the content to be foundand presented here with a medium legth copy block.',
            required: true,
          },
          ...flatMediaInput({ useCatawikiPlainEditor: false }),
          {
            name: 'ctaText',
            friendlyName: 'Button Label',
            defaultValue: 'Explore more',
            type: 'CatawikiPlainEditorWithoutBuiltInTranslation',
          },
          {
            name: 'ctaUrl',
            friendlyName: 'Button URL',
            type: 'CatawikiPlainEditorWithoutBuiltInTranslation',
          },
          {
            name: 'ctaTarget',
            friendlyName: 'Open link target',
            type: 'string',
            enum: Object.keys(LinkTarget).map((name, index) => ({
              label: capitalize(name).split('_').join(' '),
              value: Object.values(LinkTarget)[index],
            })),
            defaultValue: '_blank',
          },
        ],
      },
      backgroundColorInput(COLOR_NAME_PALE_GRAY),
      contentMobileBehaviorInput(),
    ],
    noWrap: true,
  });

  return v2Name;
}

export const componentDataFetchers = (locale: string) => ({
  [NAME]: dataFetcher(locale),
  [`v2:${NAME}`]: dataFetcher(locale),
  [`v3:${NAME}`]: dataFetcher(locale),
});
